'use client';

import { useState, useEffect } from 'react';
import { X, Shield, Sparkles, CheckCircle, Loader2 } from 'lucide-react';
import { useWaitlist } from '@/contexts/WaitlistContext';
import { API_BASE } from '@/config/api.config';

const INTEREST_OPTIONS = [
  // Compliance
  { value: 'gdpr', label: 'GDPR Compliance', category: 'Compliance' },
  { value: 'nis2', label: 'NIS2 Directive', category: 'Compliance' },
  { value: 'dora', label: 'DORA Regulation', category: 'Compliance' },
  { value: 'eprivacy', label: 'ePrivacy Directive', category: 'Compliance' },
  { value: 'ai-act', label: 'AI Act', category: 'Compliance' },
  // Standards
  { value: 'iso27001', label: 'ISO 27001', category: 'Standards' },
  { value: 'soc2', label: 'SOC 2', category: 'Standards' },
  { value: 'hipaa', label: 'HIPAA', category: 'Standards' },
  { value: 'pci-dss', label: 'PCI DSS', category: 'Standards' },
  // Other
  { value: 'alternatives', label: 'EU Software Alternatives', category: 'Features' },
  { value: 'scanning', label: 'Dependency Scanning', category: 'Features' },
  { value: 'all', label: 'Everything!', category: 'Other' },
];

export default function WaitlistModal() {
  const { isOpen, closeWaitlist, source } = useWaitlist();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState<number | null>(null);

  // Get waitlist count for social proof
  useEffect(() => {
    if (isOpen) {
      fetch(`${API_BASE}/waitlist/count`)
        .then(res => res.json())
        .then(data => setWaitlistCount(data.count))
        .catch(() => {});
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setSuccess(false);
        setError('');
      }, 300);
    }
  }, [isOpen]);

  const toggleInterest = (value: string) => {
    setInterests(prev => 
      prev.includes(value) 
        ? prev.filter(i => i !== value)
        : [...prev, value]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          name, 
          company, 
          interest: interests.join(', '),
          source 
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to join waitlist');
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={closeWaitlist}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl transform transition-all"
          onClick={e => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={closeWaitlist}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          {success ? (
            /* Success State */
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">You're on the list!</h2>
              <p className="text-gray-600 mb-4">
                Thanks for joining! We'll notify you at <span className="font-semibold">{email}</span> when we launch.
              </p>
              
              {waitlistCount && (
                <div className="bg-blue-50 rounded-lg p-3 mb-6 inline-block">
                  <p className="text-sm text-blue-700">
                    <Sparkles className="h-4 w-4 inline mr-1" />
                    You're #{waitlistCount + 1} on the waitlist!
                  </p>
                </div>
              )}

              <button
                onClick={closeWaitlist}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Continue Exploring
              </button>
            </div>
          ) : (
            /* Form State */
            <div className="p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl mb-3">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Join the Waitlist</h2>
                <p className="text-gray-600 text-sm mt-1">
                  Be first to access EU-compliant compliance tools
                </p>
                {waitlistCount !== null && waitlistCount > 10 && (
                  <p className="text-xs text-blue-600 mt-2">
                    {waitlistCount}+ people already joined
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="you@company.com"
                  />
                </div>

                {/* Name & Company */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Acme Corp"
                    />
                  </div>
                </div>

                {/* Interests - Grouped */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    What interests you? <span className="text-gray-400">(select all that apply)</span>
                  </label>
                  
                  {/* Compliance */}
                  <p className="text-xs font-semibold text-gray-500 mb-1.5 mt-3">COMPLIANCE</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {INTEREST_OPTIONS.filter(o => o.category === 'Compliance').map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleInterest(option.value)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                          interests.includes(option.value)
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {/* Standards */}
                  <p className="text-xs font-semibold text-gray-500 mb-1.5 mt-3">STANDARDS</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {INTEREST_OPTIONS.filter(o => o.category === 'Standards').map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleInterest(option.value)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                          interests.includes(option.value)
                            ? 'bg-purple-600 text-white border-purple-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {/* Features */}
                  <p className="text-xs font-semibold text-gray-500 mb-1.5 mt-3">FEATURES</p>
                  <div className="flex flex-wrap gap-2">
                    {INTEREST_OPTIONS.filter(o => o.category === 'Features' || o.category === 'Other').map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleInterest(option.value)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                          interests.includes(option.value)
                            ? 'bg-green-600 text-white border-green-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 text-red-600 px-4 py-2.5 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    'Join Waitlist'
                  )}
                </button>
              </form>

              <p className="mt-3 text-xs text-gray-500 text-center">
                No spam, ever. We respect your privacy.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
